/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-core: cycle-aligned acoustic-activity evidence, turbulent-score evidence, and
// the NRPE export-only research schema (core-viewer.js 3730-3874, 3995-4013).

import { percentile } from "./stats"
import { analysisQuality } from "./analysis-quality"
import { acousticActivityForCycles } from "./energy"
import { predictTurbulentScore, turbulentDecisionThreshold, turbulentFeatureMap } from "./turbulence"
import { MIN_ENERGY_RELIABLE_CYCLES } from "./constants"

export function visualAcousticActivityStats(ctx, strictEvidence = false) {
  const quality = analysisQuality(ctx)
  const cycles = strictEvidence
    ? quality.energyAllowed
      ? quality.energyCycles
      : []
    : quality.visualEnergyAllowed || quality.visualEnergyProvisional
    ? quality.visualEnergyCycles
    : []
  return acousticActivityForCycles(ctx.murmurTrace, ctx.payload.duration, ctx.turbulentModel, cycles)
}

// Alias kept from the prototype (4011-4013) for overlay-facing naming parity.
export function murmurEvidenceStats(ctx) {
  return visualAcousticActivityStats(ctx)
}

export function structuredTurbulenceEvidence(ctx) {
  const activity = visualAcousticActivityStats(ctx, true)
  if (!activity) return null
  const score = predictTurbulentScore(ctx.turbulentModel, activity.features)
  return {
    layer: "structured_turbulence_evidence",
    purpose: "Quality-gated phase/repeatability evidence for Research and eval use.",
    cycleAlignment: activity.cycleAlignment || null,
    cycles: activity.cycles,
    sysCyclePct: activity.sysCyclePct,
    diaCyclePct: activity.diaCyclePct,
    score,
    scoreFlag: score >= turbulentDecisionThreshold(ctx.turbulentModel),
    featureMap: turbulentFeatureMap(activity.features),
  }
}

export function nrpeExperimentSchema(ctx, activity) {
  const quietReference = {
    definition:
      "Quiet reference window is the lowest stable normalized turbulent-energy window inside the selected Timing cycle set after excluding S1/S2 marker bursts and after the same phase-window normalization used for Energy.",
    candidate_scope: "selected Timing cycle IDs only",
    window_unit: "phase window within Systole or Diastole",
    unavailable_when: [
      "fewer than the minimum selected Timing cycles are scoreable",
      "no stable low-energy phase window remains after S1/S2 burst exclusion",
      "normalization is degenerate or uniform across candidate windows",
      "input-validity or Timing gates suppress Energy",
    ],
  }
  const criteriaForUiReplacement = [
    "Normalized Reference Phase Energy is reproducible on curated/default recordings without reducing Timing/Energy audit alignment",
    "reference-unavailable null behavior is common-case safe and explainable",
    "uniform-energy flag is emitted only after normalization and is validated against artifact cases",
    "thresholds are validated against held-out local and public datasets with no regression in current Energy review behavior",
    "Reviewing Clinician wording is accepted for replacing current Energy labels",
  ]
  const quality = analysisQuality(ctx)
  const base = {
    schema: "medaica.pcg.normalized_reference_phase_energy_experiment.v1",
    version: "1.0.0",
    status: "export_only",
    ui_behavior: "does_not_replace_visible_energy_labels",
    metric_name: "Normalized Reference Phase Energy",
    metric_abbreviation: "NRPE",
    metric_description:
      "Ratio of phase-linked turbulent energy to the quietest stable reference phase window in the selected Timing cycle set.",
    quiet_reference_window: quietReference,
    selected_timing_cycle_ids: quality.selectedTimingCycleIds || [],
    scored_energy_cycle_ids: activity && activity.cycleAlignment ? activity.cycleAlignment.scoredCycleIds : [],
    nrpe: null,
    nrpe_sys: null,
    nrpe_dia: null,
    reference_available: false,
    reference_window: null,
    reference_unavailable_reason: null,
    uniform_energy_after_normalization: null,
    uniform_energy_flag_policy:
      "Emit true/false only after normalization is performed; emit null when reference or normalization is unavailable.",
    criteria_for_replacing_ui_labels_later: criteriaForUiReplacement,
  }
  if (!activity) {
    return {
      ...base,
      reference_unavailable_reason:
        quality.energyReason || quality.visualEnergyReason || "Energy activity unavailable.",
    }
  }
  const windows = (activity.phaseWindows || []).filter(
    (w) =>
      Number.isFinite(Number(w.meanEnergy)) &&
      Number.isFinite(Number(w.peakEnergy)) &&
      Number.isFinite(Number(w.duration)) &&
      Number(w.duration) > 0
  )
  if (windows.length < MIN_ENERGY_RELIABLE_CYCLES) {
    return {
      ...base,
      reference_unavailable_reason: `Needs ${MIN_ENERGY_RELIABLE_CYCLES} scoreable phase windows for Normalized Reference Phase Energy reference; found ${windows.length}.`,
    }
  }
  const means = windows.map((w) => Number(w.meanEnergy))
  const p10 = percentile(means, 0.1)
  const p90 = percentile(means, 0.9)
  const span = p90 - p10
  if (!Number.isFinite(span) || span <= 0.015) {
    return {
      ...base,
      uniform_energy_after_normalization: true,
      reference_unavailable_reason:
        "Normalized phase-window energy is effectively uniform; quiet reference unavailable.",
      normalization: {
        method: "phase_window_mean_p10_p90",
        p10_mean_energy: p10,
        p90_mean_energy: p90,
        normalized_span: span,
      },
    }
  }
  const normalizedWindows = windows.map((w) => {
    const normalizedMean = Math.max(0, Math.min(1, (Number(w.meanEnergy) - p10) / span))
    const peakToMean = Number(w.peakEnergy) / (Number(w.meanEnergy) + 1e-6)
    return {
      ...w,
      normalizedMean,
      peakToMean,
    }
  })
  const quietCutoff = percentile(
    normalizedWindows.map((w) => w.normalizedMean),
    0.25
  )
  const quietCandidates = normalizedWindows
    .filter((w) => w.normalizedMean <= quietCutoff && w.duration >= 0.03)
    .sort((a, b) => a.normalizedMean - b.normalizedMean || a.peakToMean - b.peakToMean)
  if (!quietCandidates.length) {
    return {
      ...base,
      uniform_energy_after_normalization: false,
      reference_unavailable_reason: "No stable low-energy phase window remains after normalization.",
      normalization: {
        method: "phase_window_mean_p10_p90",
        p10_mean_energy: p10,
        p90_mean_energy: p90,
        normalized_span: span,
        quiet_cutoff: quietCutoff,
      },
    }
  }
  const reference = quietCandidates[0]
  const referenceEnergy = Math.max(Number(reference.meanEnergy), 1e-6)
  const nrpeSys = activity.sysMean / referenceEnergy
  const nrpeDia = activity.diaMean / referenceEnergy
  return {
    ...base,
    nrpe: Math.max(nrpeSys, nrpeDia),
    nrpe_sys: nrpeSys,
    nrpe_dia: nrpeDia,
    reference_available: true,
    reference_window: {
      cycle_id: reference.cycleId,
      phase: reference.phase,
      start: reference.start,
      end: reference.end,
      duration: reference.duration,
      mean_energy: reference.meanEnergy,
      peak_energy: reference.peakEnergy,
      normalized_mean_energy: reference.normalizedMean,
      peak_to_mean: reference.peakToMean,
    },
    reference_unavailable_reason: null,
    uniform_energy_after_normalization: false,
    normalization: {
      method: "phase_window_mean_p10_p90",
      p10_mean_energy: p10,
      p90_mean_energy: p90,
      normalized_span: span,
      quiet_cutoff: quietCutoff,
      candidate_window_count: windows.length,
      quiet_candidate_count: quietCandidates.length,
      ratio_basis: "phase_mean_energy_divided_by_quiet_reference_mean_energy",
    },
  }
}
